import { useState } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import {
  HomeIcon,
  SettingsIcon,
  BookOpenIcon,
  CalendarIcon,
  HelpCircleIcon,
  DollarSign,
  Users,
  GraduationCap,
  CreditCard,
  University,
  Award,
  Clock,
  Link2,
  LogOut,
  AlertTriangle,
  MessageSquare,
  Mail,
  Inbox,
  ListTodo,
  Bell,
  ClipboardList,
  FileText,
  FileSignature,
  LayoutPanelLeft,
  TicketPercent,
  Package,
  PackageCheck,
  Settings2,
  KeyRound,
  Milestone,
  Stethoscope,
  Send,
  ContactRound,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/contexts/AuthContext";
import { useLogout } from "@/hooks/useLogout";
import { usePermissions } from "@/hooks/usePermissions";
import { canAny } from "@/utils/permissions";
import { ThemeToggleSimple } from "@/components/ui/theme-toggle-simple";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ChatBubble } from "@/components/chat";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { NavBadge } from "@/components/notifications/NavBadge";
import { NotificationRealtimeBridge } from "@/components/notifications/NotificationRealtimeBridge";
import { useNotificationSummary } from "@/hooks/useNotificationSummary";
import {
  countForRoutes,
  type NotificationCategory,
} from "@/lib/api/notifications";

const SIDEBAR_ROUTE_CATEGORIES: Record<string, NotificationCategory[]> = {
  "/tickets": ["Support"],
  "/queries": ["Leads"],
  "/bookstore/orders": ["Bookstore"],
  "/transcripts/requests": ["Certificate & Transcript"],
  "/education-verification/requests": ["Verification request"],
  "/name-change/requests": ["Identity"],
  "/certifications": ["Clinicals", "Certificate & Transcript"],
  "/f": ["Forms"],
  "/payments": ["Payments"],
  "/course-keys": ["Course Keys"],
  "/journey-flows": ["Journey"],
  "/clinicals": ["Clinicals"],
  "/users": ["ID's"],
  "/enrollments": ["Enrollments"],
};

/** Visible selected state for sidebar nav items (uses theme primary tokens). */
function sidebarNavButtonClass(isActive: boolean) {
  return cn(
    "w-full justify-start mb-1 transition-all",
    isActive
      ? "bg-primary-gradient text-primary-foreground font-medium shadow-sm"
      : "hover:bg-accent",
  );
}

export default function SidebarLayout() {
  const location = useLocation();
  const { user, isLoading } = useAuth();
  const { permissions, adminType } = usePermissions();
  const { userLogout, autoLogout } = useLogout();
  const { summary } = useNotificationSummary();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [sessionTimeLeft] = useState(0);

  const badge = (route: string) =>
    countForRoutes(summary, SIDEBAR_ROUTE_CATEGORIES[route] || []);

  // Use centralized session timeout configuration
  // const SESSION_TIMEOUT = config.sessionTimeout;
  // const WARNING_THRESHOLD = config.warningThreshold;

  // Get user initials for avatar fallback
  const getUserInitials = () => {
    if (!user?.fullName) return "JA";

    const nameParts = user.fullName.split(" ");
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[1][0]}`;
    }
    return nameParts[0].slice(0, 2).toUpperCase();
  };

  // Fast logout handler with confirmation (user-initiated)
  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      // Start logout immediately for better UX
      await userLogout();
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Fast auto-logout handler for session timeout (no confirmation needed)
  const handleAutoLogout = async () => {
    // Don't wait for completion - just start the process
    autoLogout('Session expired due to inactivity').catch(console.error);
  };

  // Session monitoring is handled by AuthContext - no duplicate monitoring needed

  // Activity tracking is handled by AuthContext - no duplicate tracking needed

  // Logout confirmation dialog
  const LogoutConfirmation = () => (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <SidebarMenuButton
          tooltip="Logout"
          className={cn(
            "w-full justify-start text-destructive hover:bg-destructive/10 transition-all",
            "group-data-[collapsible=icon]:w-8 group-data-[collapsible=icon]:justify-center"
          )}
          disabled={isLoggingOut}
          aria-label={isLoggingOut ? "Logging out" : "Logout"}
        >
          {isLoggingOut ? (
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent mr-2 group-data-[collapsible=icon]:mr-0"></div>
          ) : (
            <LogOut className="h-4 w-4 mr-2 group-data-[collapsible=icon]:mr-0" />
          )}
          <span className="group-data-[collapsible=icon]:hidden">
            {isLoggingOut ? "Logging out..." : "Logout"}
          </span>
        </SidebarMenuButton>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Confirm Logout</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to log out? Any unsaved changes will be lost.
            <br />
            <br />
            <strong>Current User:</strong> {user?.fullName} ({user?.email})
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoggingOut}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isLoggingOut ? "Logging out..." : "Logout"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  // Session warning dialog
  const SessionWarningDialog = () => (
    <AlertDialog open={showSessionWarning} onOpenChange={setShowSessionWarning}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warning" />
            Session Expiring Soon
          </AlertDialogTitle>
          <AlertDialogDescription>
            Your session will expire in{" "}
            <strong>
              {sessionTimeLeft} minute{sessionTimeLeft > 1 ? "s" : ""}
            </strong>
            .
            <br />
            <br />
            Please save your work and log in again if you need to continue.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setShowSessionWarning(false)}>
            Continue Working
          </AlertDialogCancel>
          <AlertDialogAction onClick={() => handleAutoLogout()}>
            Logout Now
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  const isFormEditRoute = /^\/f\/[^/]+\/edit$/.test(location.pathname);
  const isEmailOpsEditorRoute = /^\/email-ops\/templates\/[^/]+\/edit$/.test(
    location.pathname
  );
  const isFullBleedEditRoute = isFormEditRoute || isEmailOpsEditorRoute;
  const isPaymentsRoute = location.pathname === "/payments";

  return (
    <SidebarProvider>
      <NotificationRealtimeBridge />
      <Sidebar collapsible="icon" className="border-r border-sidebar-border bg-sidebar">
        <SidebarHeader className="px-6 py-4 flex flex-col items-center group-data-[collapsible=icon]:px-2">
          <div className="flex items-center justify-center w-full group-data-[collapsible=icon]:justify-center">
            <img src="/logo.png" alt="Join Americation" className="max-h-10 w-auto object-contain" />
          </div>
          <Separator className="my-4 bg-sidebar-border" />
        </SidebarHeader>

        <SidebarContent className="px-3">
          <SidebarMenu>
            {/* Main Section */}
            {/* Main Section */}
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 pl-4 group-data-[collapsible=icon]:hidden">
              Main
            </div>
            {canAny(permissions, ['dashboard.analytics.view'], adminType) && (
              <NavLink to="/" end className="block">
                <SidebarMenuButton
                  tooltip="Dashboard"
                  isActive={location.pathname === "/"}
                  className={sidebarNavButtonClass(location.pathname === "/")}
                >
                  <HomeIcon className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                  <span>Dashboard</span>
                </SidebarMenuButton>
              </NavLink>
            )}
            <NavLink to="/notifications" className="block">
              <SidebarMenuButton
                tooltip="Notifications"
                isActive={location.pathname.startsWith("/notifications")}
                className={sidebarNavButtonClass(location.pathname.startsWith("/notifications"))}
              >
                <Inbox className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                <span>Notifications</span>
                <NavBadge count={summary?.unread ?? 0} />
              </SidebarMenuButton>
            </NavLink>
            {canAny(permissions, ['admin.settings.view', 'dashboard.analytics.view'], adminType) && (
              <>
                <NavLink to="/tasks" className="block">
                  <SidebarMenuButton
                    tooltip="Tasks"
                    isActive={location.pathname.startsWith("/tasks")}
                    className={sidebarNavButtonClass(location.pathname.startsWith("/tasks"))}
                  >
                    <ListTodo className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                    <span>Tasks</span>
                  </SidebarMenuButton>
                </NavLink>
                <NavLink to="/reminders" className="block">
                  <SidebarMenuButton
                    tooltip="Reminders"
                    isActive={location.pathname.startsWith("/reminders")}
                    className={sidebarNavButtonClass(location.pathname.startsWith("/reminders"))}
                  >
                    <Bell className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                    <span>Reminders</span>
                  </SidebarMenuButton>
                </NavLink>
              </>
            )}

            {/* Education Section */}
            {canAny(permissions, [
              'programs.published.view', 'programs.draft.view',
              'certificates.main.view', 'users.main.view',
              'categories.main.view', 'subscriptions.main.view'
            ], adminType) && (
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-6 mb-2 pl-4 group-data-[collapsible=icon]:hidden">
                Education
              </div>
            )}

            {canAny(permissions, ['programs.published.view', 'programs.draft.view'], adminType) && (
              <NavLink to="/programs-v2" className="block">
                <SidebarMenuButton
                  tooltip="Programs"
                  isActive={location.pathname.startsWith("/programs-v2")}
                  className={sidebarNavButtonClass(
                    location.pathname.startsWith("/programs-v2"),
                  )}
                >
                  <University className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                  <span>Programs</span>
                </SidebarMenuButton>
              </NavLink>
            )}

            {canAny(permissions, ['courseKeys.main.view', 'programs.published.view'], adminType) && (
              <NavLink to="/course-keys" className="block">
                <SidebarMenuButton
                  tooltip="Course Key"
                  isActive={location.pathname.startsWith("/course-keys")}
                  className={sidebarNavButtonClass(
                    location.pathname.startsWith("/course-keys"),
                  )}
                >
                  <KeyRound className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                  <span>Course Key</span>
                  <NavBadge count={badge("/course-keys")} />
                </SidebarMenuButton>
              </NavLink>
            )}

            {canAny(permissions, ['programs.published.edit', 'programs.draft.edit'], adminType) && (
              <NavLink to="/programs/home-page-layout" className="block">
                <SidebarMenuButton
                  tooltip="Home page layout"
                  isActive={location.pathname.startsWith("/programs/home-page-layout")}
                  className={sidebarNavButtonClass(
                    location.pathname.startsWith("/programs/home-page-layout"),
                  )}
                >
                  <LayoutPanelLeft className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                  <span>Home page layout</span>
                </SidebarMenuButton>
              </NavLink>
            )}

            {canAny(permissions, ['certificates.main.view'], adminType) && (
              <NavLink to="/certifications" className="block">
                <SidebarMenuButton
                  tooltip="Certificate"
                  isActive={location.pathname.startsWith("/certifications")}
                  className={sidebarNavButtonClass(
                    location.pathname.startsWith("/certifications"),
                  )}
                >
                  <Award className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                  <span>Certificate</span>
                  <NavBadge count={badge("/certifications")} />
                </SidebarMenuButton>
              </NavLink>
            )}

            {canAny(permissions, ['transcripts.main.view'], adminType) && (
              <>
                <NavLink to="/transcripts/requests" className="block">
                  <SidebarMenuButton
                    tooltip="Transcript requests"
                    isActive={location.pathname.startsWith("/transcripts/requests")}
                    className={sidebarNavButtonClass(
                      location.pathname.startsWith("/transcripts/requests"),
                    )}
                  >
                    <FileText className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                    <span>Transcript requests</span>
                    <NavBadge count={badge("/transcripts/requests")} />
                  </SidebarMenuButton>
                </NavLink>
                <NavLink to="/transcripts/templates" className="block">
                  <SidebarMenuButton
                    tooltip="Transcript templates"
                    isActive={location.pathname.startsWith("/transcripts/templates")}
                    className={sidebarNavButtonClass(
                      location.pathname.startsWith("/transcripts/templates"),
                    )}
                  >
                    <FileText className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                    <span>Transcript templates</span>
                  </SidebarMenuButton>
                </NavLink>
                <NavLink to="/coupons" className="block">
                  <SidebarMenuButton
                    tooltip="Coupons"
                    isActive={location.pathname.startsWith("/coupons")}
                    className={sidebarNavButtonClass(
                      location.pathname.startsWith("/coupons"),
                    )}
                  >
                    <TicketPercent className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                    <span>Coupons</span>
                  </SidebarMenuButton>
                </NavLink>
                <NavLink to="/name-change/requests" className="block">
                  <SidebarMenuButton
                    tooltip="Legal name changes"
                    isActive={location.pathname.startsWith("/name-change/requests")}
                    className={sidebarNavButtonClass(
                      location.pathname.startsWith("/name-change/requests"),
                    )}
                  >
                    <FileSignature className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                    <span>Legal name changes</span>
                    <NavBadge count={badge("/name-change/requests")} />
                  </SidebarMenuButton>
                </NavLink>
                <NavLink to="/education-verification/requests" className="block">
                  <SidebarMenuButton
                    tooltip="Education verification"
                    isActive={location.pathname.startsWith("/education-verification/requests")}
                    className={sidebarNavButtonClass(
                      location.pathname.startsWith("/education-verification/requests"),
                    )}
                  >
                    <GraduationCap className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                    <span>Education verification</span>
                    <NavBadge count={badge("/education-verification/requests")} />
                  </SidebarMenuButton>
                </NavLink>
              </>
            )}

            {canAny(
              permissions,
              [
                'bookstore.products.view',
                'bookstore.orders.view',
                'bookstore.settings.view',
              ],
              adminType,
            ) && (
              <>
                {canAny(permissions, ['bookstore.products.view'], adminType) && (
                  <NavLink to="/bookstore/products" className="block">
                    <SidebarMenuButton
                      tooltip="Book Store products"
                      isActive={location.pathname.startsWith("/bookstore/products")}
                      className={sidebarNavButtonClass(
                        location.pathname.startsWith("/bookstore/products"),
                      )}
                    >
                      <Package className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                      <span>Book Store products</span>
                    </SidebarMenuButton>
                  </NavLink>
                )}
                {canAny(permissions, ['bookstore.orders.view'], adminType) && (
                  <NavLink to="/bookstore/orders" className="block">
                    <SidebarMenuButton
                      tooltip="Book Store orders"
                      isActive={location.pathname.startsWith("/bookstore/orders")}
                      className={sidebarNavButtonClass(
                        location.pathname.startsWith("/bookstore/orders"),
                      )}
                    >
                      <PackageCheck className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                      <span>Book Store orders</span>
                      <NavBadge count={badge("/bookstore/orders")} />
                    </SidebarMenuButton>
                  </NavLink>
                )}
                {canAny(permissions, ['bookstore.settings.view'], adminType) && (
                  <NavLink to="/bookstore/settings" className="block">
                    <SidebarMenuButton
                      tooltip="Book Store settings"
                      isActive={location.pathname.startsWith("/bookstore/settings")}
                      className={sidebarNavButtonClass(
                        location.pathname.startsWith("/bookstore/settings"),
                      )}
                    >
                      <Settings2 className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                      <span>Book Store settings</span>
                    </SidebarMenuButton>
                  </NavLink>
                )}
              </>
            )}

            {canAny(permissions, ['users.main.view'], adminType) && (
              <>
                <NavLink to="/enrollments" className="block">
                  <SidebarMenuButton
                    tooltip="Enrollments"
                    isActive={location.pathname.startsWith("/enrollments")}
                    className={sidebarNavButtonClass(
                      location.pathname.startsWith("/enrollments"),
                    )}
                  >
                    <GraduationCap className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                    <span>Enrollments</span>
                    <NavBadge count={badge("/enrollments")} />
                  </SidebarMenuButton>
                </NavLink>
                <NavLink to="/users" className="block">
                  <SidebarMenuButton
                    tooltip="Users"
                    isActive={location.pathname.startsWith("/users")}
                    className={sidebarNavButtonClass(
                      location.pathname.startsWith("/users"),
                    )}
                  >
                    <Users className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                    <span>Users</span>
                    <NavBadge count={badge("/users")} />
                  </SidebarMenuButton>
                </NavLink>
              </>
            )}

            {canAny(permissions, ['categories.main.view'], adminType) && (
              <NavLink to="/categories" className="block">
                <SidebarMenuButton
                  tooltip="Categories"
                  isActive={location.pathname.startsWith("/categories")}
                  className={sidebarNavButtonClass(
                    location.pathname.startsWith("/categories"),
                  )}
                >
                  <BookOpenIcon className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                  <span>Categories</span>
                </SidebarMenuButton>
              </NavLink>
            )}

            {canAny(permissions, ['subscriptions.main.view'], adminType) && (
              <NavLink to="/subscriptions" className="block">
                <SidebarMenuButton
                  tooltip="Subscriptions"
                  isActive={location.pathname.startsWith("/subscriptions")}
                  className={sidebarNavButtonClass(
                    location.pathname.startsWith("/subscriptions"),
                  )}
                >
                  <DollarSign className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                  <span>Subscriptions</span>
                </SidebarMenuButton>
              </NavLink>
            )}

            {canAny(permissions, ['dashboard.analytics.view'], adminType) && (
              <NavLink to="/schedule" className="block">
                <SidebarMenuButton
                  tooltip="Schedule"
                  isActive={location.pathname.startsWith("/schedule")}
                  disabled={true}
                  className={sidebarNavButtonClass(
                    location.pathname.startsWith("/schedule"),
                  )}
                >
                  <CalendarIcon className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                  <span>Schedule</span>
                </SidebarMenuButton>
              </NavLink>
            )}

            {/* Payments Section */}
            {canAny(permissions, ['payments.main.view', 'payments.authorization.view'], adminType) && (
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-6 mb-2 pl-4 group-data-[collapsible=icon]:hidden">
                Payments
              </div>
            )}

            {canAny(permissions, ['payments.main.view', 'payments.authorization.view'], adminType) && (
              <NavLink to="/payments" className="block">
                <SidebarMenuButton
                  tooltip="Admin Payments"
                  isActive={
                    location.pathname === "/payments" ||
                    (location.pathname.startsWith("/payments/") &&
                      !location.pathname.startsWith("/payments/checkout-tracking") &&
                      !location.pathname.startsWith("/payments/subscription-setup"))
                  }
                  className={sidebarNavButtonClass(
                    location.pathname === "/payments" ||
                      (location.pathname.startsWith("/payments/") &&
                        !location.pathname.startsWith("/payments/checkout-tracking") &&
                        !location.pathname.startsWith("/payments/subscription-setup")),
                  )}
                >
                  <CreditCard className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                  <span>Admin Payments</span>
                  <NavBadge count={badge("/payments")} />
                </SidebarMenuButton>
              </NavLink>
            )}

            {canAny(permissions, ['payments.main.view', 'payments.authorization.view'], adminType) && (
              <NavLink to="/payments/subscription-setup" className="block">
                <SidebarMenuButton
                  tooltip="Subscription Setup"
                  isActive={location.pathname.startsWith("/payments/subscription-setup")}
                  className={sidebarNavButtonClass(
                    location.pathname.startsWith("/payments/subscription-setup"),
                  )}
                >
                  <Link2 className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                  <span>Subscription Setup</span>
                </SidebarMenuButton>
              </NavLink>
            )}

            {canAny(permissions, ['checkoutTracking.view'], adminType) && (
              <NavLink to="/payments/checkout-tracking" className="block">
                <SidebarMenuButton
                  tooltip="Checkout Tracking"
                  isActive={location.pathname.startsWith("/payments/checkout-tracking")}
                  className={sidebarNavButtonClass(
                    location.pathname.startsWith("/payments/checkout-tracking"),
                  )}
                >
                  <Clock className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                  <span>Checkout Tracking</span>
                </SidebarMenuButton>
              </NavLink>
            )}



            {/* Support Section */}
            {canAny(permissions, ['supportTickets.main.view', 'supportTickets.chat.view'], adminType) && (
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-6 mb-2 pl-4 group-data-[collapsible=icon]:hidden">
                Support
              </div>
            )}

            {canAny(permissions, ['supportTickets.main.view', 'supportTickets.chat.view'], adminType) && (
              <NavLink to="/tickets" className="block">
                <SidebarMenuButton
                  tooltip="Support Tickets"
                  isActive={location.pathname.startsWith("/tickets")}
                  className={sidebarNavButtonClass(location.pathname.startsWith("/tickets"))}
                >
                  <MessageSquare className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                  <span>Support Tickets</span>
                  <NavBadge count={badge("/tickets")} />
                </SidebarMenuButton>
              </NavLink>
            )}

            {canAny(permissions, ['supportTickets.main.view', 'supportTickets.chat.view'], adminType) && (
              <NavLink to="/queries" className="block">
                <SidebarMenuButton
                  tooltip="Queries (leads)"
                  isActive={location.pathname.startsWith("/queries")}
                  className={sidebarNavButtonClass(location.pathname.startsWith("/queries"))}
                >
                  <Inbox className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                  <span>Queries</span>
                  <NavBadge count={badge("/queries")} />
                </SidebarMenuButton>
              </NavLink>
            )}

            {canAny(permissions, ['leads.inbox.view'], adminType) && (
              <NavLink to="/leads" className="block">
                <SidebarMenuButton
                  tooltip="All captured leads (CRM)"
                  isActive={location.pathname.startsWith("/leads")}
                  className={sidebarNavButtonClass(location.pathname.startsWith("/leads"))}
                >
                  <ContactRound className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                  <span>Leads</span>
                </SidebarMenuButton>
              </NavLink>
            )}

            {canAny(permissions, ['supportTickets.main.view', 'supportTickets.chat.view'], adminType) && (
              <NavLink to="/faqs" className="block">
                <SidebarMenuButton
                  tooltip="Bot FAQs"
                  isActive={location.pathname.startsWith("/faqs")}
                  className={sidebarNavButtonClass(location.pathname.startsWith("/faqs"))}
                >
                  <HelpCircleIcon className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                  <span>FAQs</span>
                </SidebarMenuButton>
              </NavLink>
            )}

            {canAny(
              permissions,
              ['supportTickets.main.view', 'supportTickets.chat.view', 'admin.settings.view'],
              adminType,
            ) && (
              <NavLink to="/articles" className="block">
                <SidebarMenuButton
                  tooltip="Blog articles"
                  isActive={location.pathname.startsWith("/articles")}
                  className={sidebarNavButtonClass(location.pathname.startsWith("/articles"))}
                >
                  <FileText className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                  <span>Articles</span>
                </SidebarMenuButton>
              </NavLink>
            )}

            {canAny(permissions, ['supportTickets.main.view', 'supportTickets.chat.view'], adminType) && (
              <NavLink to="/f" className="block">
                <SidebarMenuButton
                  tooltip="Forms"
                  isActive={
                    location.pathname === "/f" || location.pathname.startsWith("/f/")
                  }
                  className={sidebarNavButtonClass(
                    location.pathname === "/f" || location.pathname.startsWith("/f/"),
                  )}
                >
                  <ClipboardList className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                  <span>Forms</span>
                  <NavBadge count={badge("/f")} />
                </SidebarMenuButton>
              </NavLink>
            )}

            {canAny(permissions, ['supportTickets.main.view', 'supportTickets.chat.view'], adminType) && (
              <NavLink to="/qualification-flow" className="block">
                <SidebarMenuButton
                  tooltip="Lead qualification flow"
                  isActive={location.pathname.startsWith("/qualification-flow")}
                  className={sidebarNavButtonClass(
                    location.pathname.startsWith("/qualification-flow"),
                  )}
                >
                  <HelpCircleIcon className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                  <span>Lead Qualification</span>
                </SidebarMenuButton>
              </NavLink>
            )}

            {canAny(permissions, ['journeys.flows.view', 'journeys.review.view'], adminType) && (
              <NavLink to="/journey-flows" className="block">
                <SidebarMenuButton
                  tooltip="Student journey flows"
                  isActive={location.pathname.startsWith("/journey-flows")}
                  className={sidebarNavButtonClass(
                    location.pathname.startsWith("/journey-flows"),
                  )}
                >
                  <Milestone className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                  <span>Journey Flows</span>
                  <NavBadge count={badge("/journey-flows")} />
                </SidebarMenuButton>
              </NavLink>
            )}

            {canAny(permissions, ['journeys.review.view', 'certificates.main.view'], adminType) && (
              <NavLink to="/clinicals" className="block">
                <SidebarMenuButton
                  tooltip="Clinicals scheduling"
                  isActive={location.pathname.startsWith("/clinicals")}
                  className={sidebarNavButtonClass(
                    location.pathname.startsWith("/clinicals"),
                  )}
                >
                  <Stethoscope className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                  <span>Clinicals</span>
                  <NavBadge count={badge("/clinicals")} />
                </SidebarMenuButton>
              </NavLink>
            )}

            {canAny(permissions, ['leadAutomations.flows.view', 'leadAutomations.activity.view'], adminType) && (
              <NavLink to="/lead-automations" className="block">
                <SidebarMenuButton
                  tooltip="Lead automation email sequences"
                  isActive={location.pathname.startsWith("/lead-automations")}
                  className={sidebarNavButtonClass(
                    location.pathname.startsWith("/lead-automations"),
                  )}
                >
                  <Send className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                  <span>Lead Automations</span>
                </SidebarMenuButton>
              </NavLink>
            )}

            {/* System Section */}
            {canAny(permissions, ['admin.settings.view', 'admin.staff.view', 'admin.roles.view'], adminType) && (
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-6 mb-2 pl-4 group-data-[collapsible=icon]:hidden">
                System
              </div>
            )}

            {canAny(permissions, ['admin.settings.view'], adminType) && (
              <NavLink to="/settings" className="block">
                <SidebarMenuButton
                  tooltip="Settings"
                  isActive={location.pathname === "/settings"}
                  className={sidebarNavButtonClass(location.pathname === "/settings")}
                >
                  <SettingsIcon className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                  <span>Settings</span>
                </SidebarMenuButton>
              </NavLink>
            )}

            {canAny(permissions, ['admin.settings.view'], adminType) && (
              <>
                <NavLink to="/email/templates" className="block">
                  <SidebarMenuButton
                    tooltip="Email Templates"
                    isActive={location.pathname.startsWith("/email/templates")}
                    className={sidebarNavButtonClass(
                      location.pathname.startsWith("/email/templates"),
                    )}
                  >
                    <Mail className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                    <span>Email Templates</span>
                  </SidebarMenuButton>
                </NavLink>
              </>
            )}

            {adminType === "ADMIN" && (
              <NavLink to="/email-ops" className="block">
                <SidebarMenuButton
                  tooltip="Email Management"
                  isActive={location.pathname.startsWith("/email-ops")}
                  className={sidebarNavButtonClass(
                    location.pathname.startsWith("/email-ops"),
                  )}
                >
                  <Inbox className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                  <span>Email Management</span>
                </SidebarMenuButton>
              </NavLink>
            )}

            {canAny(permissions, ['admin.staff.view', 'admin.roles.view'], adminType) && (
              <NavLink to="/settings/staff-roles" className="block">
                <SidebarMenuButton
                  tooltip="Staff & Roles"
                  isActive={location.pathname.startsWith("/settings/staff-roles")}
                  className={sidebarNavButtonClass(
                    location.pathname.startsWith("/settings/staff-roles"),
                  )}
                >
                  <Users className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                  <span>Staff & Roles</span>
                </SidebarMenuButton>
              </NavLink>
            )}

            <NavLink to="/help" className="block">
              <SidebarMenuButton
                tooltip="Help & Support"
                isActive={location.pathname.startsWith("/help")}
                className={sidebarNavButtonClass(location.pathname.startsWith("/help"))}
              >
                <HelpCircleIcon className="h-5 w-5 mr-3 group-data-[collapsible=icon]:mr-0" />
                <span>Help & Support</span>
              </SidebarMenuButton>
            </NavLink>
          </SidebarMenu>
        </SidebarContent>

        <SidebarFooter className="p-4 mt-auto border-t border-sidebar-border group-data-[collapsible=icon]:p-2">
          <div className="flex items-center space-x-3 mb-4 group-data-[collapsible=icon]:mb-2 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:space-x-0">
            <Avatar>
              <AvatarImage
                src="/avatars/admin.png"
                alt={user?.fullName || "Admin"}
              />
              <AvatarFallback>{getUserInitials()}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0 group-data-[collapsible=icon]:hidden">
              <p className="text-sm font-medium truncate">
                {user?.fullName || "Admin User"}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {user?.email || "admin@example.com"}
              </p>
              {/* Session status indicator */}
              <div className="flex items-center gap-1 mt-1">
                <div className="w-2 h-2 rounded-full bg-success animate-pulse"></div>
                <span className="text-xs text-muted-foreground">Active Session</span>
              </div>
            </div>
            <NotificationBell />
          </div>

          <LogoutConfirmation />
        </SidebarFooter>
      </Sidebar>

      <main
        className={cn(
          "flex min-h-0 flex-1 flex-col",
          isFullBleedEditRoute
            ? "overflow-hidden p-0"
            : isPaymentsRoute
              ? "overflow-y-auto p-0"
              : "overflow-y-auto"
        )}
      >
        {/* Session timeout warning banner */}
        {showSessionWarning && (
          <div className="mx-4 mt-4 mb-0 sm:mx-6 p-4 bg-accent border border-primary/20 rounded-lg">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-primary" />
              <div className="flex-1">
                <h3 className="font-medium text-foreground">
                  Session Expiring Soon
                </h3>
                <p className="text-sm text-muted-foreground">
                  Your session will expire in {sessionTimeLeft} minute{sessionTimeLeft > 1 ? 's' : ''}. 
                  Please save your work and log in again if needed.
                </p>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowSessionWarning(false)}
                className="border-primary/30 text-foreground hover:bg-accent"
              >
                Dismiss
              </Button>
            </div>
          </div>
        )}
        
        {/* Authentication loading state */}
        {isLoading && (
          <div className="mx-4 mt-4 mb-0 sm:mx-6 p-4 bg-accent/60 border border-primary/15 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
              <div className="flex-1">
                <h3 className="font-medium text-foreground">
                  Checking Authentication...
                </h3>
                <p className="text-sm text-muted-foreground">
                  Please wait while we verify your session
                </p>
              </div>
            </div>
          </div>
        )}
        
        {isFullBleedEditRoute ? (
          <div className="absolute left-3 top-3 z-40 flex items-center gap-2">
            <SidebarTrigger className="scale-125" />
            <ThemeToggleSimple />
          </div>
        ) : isPaymentsRoute ? null : (
          <div className="admin-main-toolbar sticky top-0 z-30 flex shrink-0 items-center justify-between px-4 py-3 sm:px-6">
            <SidebarTrigger className="scale-125" />
            <div className="flex items-center space-x-2">
              <ThemeToggleSimple />
            </div>
          </div>
        )}
        <div
          className={cn(
            "min-h-0",
            isFullBleedEditRoute
              ? "flex flex-1 flex-col overflow-hidden"
              : isPaymentsRoute
                ? undefined
                : "px-4 pb-6 pt-4 sm:px-6 sm:pb-8"
          )}
        >
          <Outlet />
        </div>
      </main>
      <SessionWarningDialog />
      <ChatBubble />
    </SidebarProvider>
  );
}

// // Helper functions to get page titles and descriptions
// function getPageTitle(pathname: string): string {
//   switch (pathname) {
//     case "/":
//       return "Dashboard";
//     case "/users":
//       return "User Management";
//     case "/settings":
//       return "Settings";
//     case "/courses":
//       return "Courses";
//     case "/schedule":
//       return "Schedule";
//     case "/help":
//       return "Help & Support";
//     case "/user-payment-history":
//       return "Payment History";
//     case "/user-upcoming-payments":
//       return "Upcoming Payments";
//     case "/certifications":
//       return "Certifications";
//     default:
//       return "Join Americation Admin";
//   }
// }

// function getPageDescription(pathname: string): string {
//   switch (pathname) {
//     case "/":
//       return "Overview of key metrics and activity";
//     case "/users":
//       return "View and manage user accounts and profiles";
//     case "/settings":
//       return "Configure system preferences and settings";
//     case "/courses":
//       return "Manage course offerings and content";
//     case "/schedule":
//       return "View and manage class schedules";
//     case "/help":
//       return "Get assistance and documentation";
//     case "/user-payment-history":
//       return "View your complete payment history and transactions";
//     case "/user-upcoming-payments":
//       return "Manage your scheduled payments and payment plans";
//     case "/certifications":
//       return "Manage national certifications and related data";
//     default:
//       return "Administrative portal for Americation";
//   }
// }
